// featureFlags.js
// Deployment-level gates for surfaces whose BACKEND IS NOT YET BUILT.
//
//   SafetyHealth.js       → /api/safety/*                            (5/5 calls
//                            dead; only /api/mobile/safety/* exists, under a
//                            different prefix and a different contract)
//   EnhancedEventPlanning.js → /api/venues, /api/event-templates      (absent
//                            from all 287 OpenAPI paths; /api/events/enhanced
//                            resolves as /api/events/{event_id} and 404s)
//
// These are hidden by DEFAULT so SMB-operator pilots never reach a screen that
// 404s on every call. This is intentionally a BUILD-TIME decision, not per-org
// module config (`modules_enabled`): those surfaces have no backend at all for
// ANY org, so per-tenant toggling would be misleading. The public guest booking
// widget (GuestRoomBooking → /api/public/rooms/*) is backed and is NOT gated here.
//
// Re-enable a surface (e.g. for a sales demo) by setting, at build time:
//   REACT_APP_ENABLE_SAFETY=true          → re-enables /safety
//   REACT_APP_ENABLE_ENHANCED_EVENTS=true → re-enables /enhanced-events
//
// When the backend ships, DELETE the corresponding entry below — the surface then
// reverts to normal per-org gating. Rooms and delivery were removed this way on
// 2026-08-13. `REACT_APP_ENABLE_ROOMS` / `REACT_APP_ENABLE_DELIVERY` are now inert
// and can be dropped from compose files at leisure; they no longer gate anything.
//
// A gate here is a claim that NO backend exists. boundary.spec.mjs enforces that
// claim against server.py's mounted routers, so a stale entry now fails the suite
// instead of silently hiding shipped work.

const truthy = (v) => String(v).toLowerCase() === 'true';

// gate-key (matches the nav item `gate` and the route path) → enabled?
// Default false = hidden until the env override flips it on.
const SURFACE_ENABLED = {
  // 'room-booking' and 'delivery' were REMOVED here on 2026-08-13, per this
  // file's own rule: when the backend ships, delete the entry and let normal
  // per-org gating take over. Both backends are mounted in server.py
  // (rooms_router, delivery_router) and both are exercised by backend tests
  // (test_rooms_api.py, test_procurement_delivery_api.py).
  //
  // Delivery is the one that mattered: delivery_api.py shipped 10 real routes
  // with 22 DB operations via a parallel-track auto-commit, and all three paths
  // DeliveryManagement.js calls (/orders, /drivers, /assignments) exist — but
  // the gate's justifying COMMENT ("no /api/delivery/* router in server.py")
  // could not regress when it stopped being true, so the screen stayed dark
  // with working, tested code behind it.
  //
  // That is now caught: boundary.spec.mjs asserts no gate key here maps to a
  // router that IS mounted. Adding an entry for a surface whose backend exists
  // will fail the suite.
  // Found 2026-07-30 by the Phase 0 browser gate. These two were previously
  // masked: they fell back to an absolute http://localhost:8000, so every call
  // hit whatever occupied that port and returned 401 — which read as an auth
  // problem rather than "these endpoints were never built". Once the calls
  // were corrected to same-origin they returned honest 404s.
  'safety': truthy(process.env.REACT_APP_ENABLE_SAFETY),
  'enhanced-events': truthy(process.env.REACT_APP_ENABLE_ENHANCED_EVENTS),
};

// True only for a KNOWN unbuilt surface that is currently disabled. Unknown
// gate keys return false here so normal per-org gating is untouched.
export const isUnbuiltSurface = (gate) =>
  Object.prototype.hasOwnProperty.call(SURFACE_ENABLED, gate) && !SURFACE_ENABLED[gate];

// Route-guard convenience: may this surface be mounted at all?
export const surfaceEnabled = (gate) => !isUnbuiltSurface(gate);
